import { v4 as uuid } from 'uuid';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosError, AxiosResponse, CancelTokenSource } from 'axios';
import { ERROR_CODES, errors, ErrorConfig, ApiError } from './errors';

export type HttpConfig = {
	baseUrl?: string;
	defaultHeaders?: Record<string, string | number | boolean>;
	tokenHeaderKey?: string;
	getToken?(): Promise<string | undefined>;
};

export type OrchestratedRequest<T = undefined> = {
	requestId: string;
	requestComplete: boolean;
	source: CancelTokenSource;
	config: AxiosRequestConfig;
	fetch(): Promise<T>;
	abort(): void;
};

export class HttpClient {
	_baseUrl: string;
	_headers: any;
	_requests: Record<string, OrchestratedRequest<any>>;
	_axiosInstance: AxiosInstance;

	constructor(httpConfig: HttpConfig) {
		this._baseUrl = httpConfig.baseUrl || '';
		this._headers = httpConfig.defaultHeaders || {};
		this._requests = {};

		this._axiosInstance = axios.create();
		this._axiosInstance.interceptors.request.use(
			async (options) => {
				if (httpConfig.tokenHeaderKey && httpConfig.getToken) {
					const token = await httpConfig.getToken();
					if (token) {
						options.headers[httpConfig.tokenHeaderKey] = token;
					}
				}
				return options;
			},
			(error) => {
				console.error('Error obtaining accessToken:', error);
			}
		);
	}

	abortRequest(requestId: string) {
		const request = this._requests[requestId];

		if (request) {
			request.abort();
		}
	}

	async _fetch(config: AxiosRequestConfig) {
		try {
			const response: AxiosResponse = await this._axiosInstance(config);
			return response;
		} catch (error) {
			if (axios.isCancel(error)) {
				throw errors[ERROR_CODES.REQUEST_ABORTED](error);
			} else {
				const formattedError = this._getFormattedError(error);
				throw formattedError;
			}
		}
	}

	_getFormattedError(error: AxiosError): ErrorConfig {
		const errorFromApi: ApiError = error?.response?.data;

		if (errorFromApi) {
			const formattedApiError: ErrorConfig = {
				code: errorFromApi.code,
				message: errorFromApi.message,
				apiError: errorFromApi,
				axiosError: error,
			};

			return formattedApiError;
		}

		return errors[ERROR_CODES.GENERIC](error);
	}

	orchestrateRequest<T>(requestConfig: AxiosRequestConfig) {
		const orchestratedRequest: OrchestratedRequest<T> = {
			requestId: uuid(),
			requestComplete: false,
		} as any;

		orchestratedRequest.fetch = async () => {
			const source = axios.CancelToken.source();

			orchestratedRequest.requestComplete = false;
			orchestratedRequest.source = source;
			orchestratedRequest.config = {
				...requestConfig,
				baseURL: this._baseUrl,
				headers: {
					...this._headers,
					...requestConfig.headers,
				},
				cancelToken: source.token,
				...(requestConfig.data ? { data: JSON.stringify(requestConfig.data) } : {}),
			};

			this._requests[orchestratedRequest.requestId] = orchestratedRequest;

			const { data } = await this._fetch(orchestratedRequest.config);

			orchestratedRequest.requestComplete = true;
			delete this._requests[orchestratedRequest.requestId];

			return data as T;
		};

		orchestratedRequest.abort = () => {
			if (orchestratedRequest.requestComplete) return;

			orchestratedRequest.source.cancel();

			console.group(`Request aborted: ${orchestratedRequest.config.baseURL}${orchestratedRequest.config.url}`);
			console.groupEnd();

			orchestratedRequest.requestComplete = true;
			delete this._requests[orchestratedRequest.requestId];
		};

		return orchestratedRequest;
	}
}
